// dependencies
import PropTypes from "prop-types";
import React, { useRef, useLayoutEffect } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";

// utils
import get from "Utils/get";

// style
import css from "../Table.styl";

const TableHead = (props) => {
	const {
		columns,
		subheaders,
		colWidths,
		activeSort,
		onColumnClick,
		highlightColumn,
		onUpdateHighlight,
		defaultCellClassName,
		activeSortDirection,
		currentScreenWidth,
	} = props;

	const highlightColumnRef = useRef(null);

	const handleColumnClick = (column) => {
		// gets the next direction value, toggles to the opposite direction
		// if the user clicks the active filter
		const nextDirection =
			(activeSortDirection === "desc" || activeSortDirection === "descending") && activeSort === column
				? "asc"
				: "desc";
		// update parent handler
		onColumnClick(column, nextDirection);
	};

	useLayoutEffect(() => {
		onUpdateHighlight(highlightColumnRef);
	}, [highlightColumn]);

	return (
		<div className={css.tableHead} style={{ gridTemplateColumns: colWidths }}>
			{columns.map((col) => {
				const {
					id,
					sortName = id,
					isSortable = true,
					headCellClass = null,
					renderHeadCell = null,
					centerHeader = false,
					mobileViewHideColumn,
				} = col;
				const hasFilter = activeSort === sortName;
				const hasHighlight = highlightColumn === id;
				const cellClassName = headCellClass || defaultCellClassName;

				if (renderHeadCell) {
					return (
						<div
							ref={hasHighlight ? highlightColumnRef : null}
							key={id}
							className={`${css.column} ${hasFilter ? css.hasFilter : ""} ${
								isSortable ? css.isSortable : ""
							} ${cellClassName}`}
						>
							{renderHeadCell()}
						</div>
					);
				}
				return (
					<div key={id} className={mobileViewHideColumn > currentScreenWidth ? css.hiddenColumn : ""}>
						<div
							ref={hasHighlight ? highlightColumnRef : null}
							className={`${css.column} ${hasFilter ? css.hasFilter : ""} ${
								isSortable ? css.isSortable : ""
							} ${cellClassName}`}
							style={subheaders[id] || centerHeader ? { textAlign: "center" } : {}}
						>
							<span
								onClick={() => (isSortable ? handleColumnClick(sortName) : null)}
								className={css.span}
							>
								<div className={css.label}>{get(["label"], col)}</div>
								{hasFilter && isSortable ? (
									<div className={css.sortIconWrapper}>
										<div
											className={`${css.sortIcon} ${
												activeSortDirection === "asc" || activeSortDirection === "ascending"
													? css.isActive
													: ""
											}`}
										>
											<ArrowUp size={16} />
										</div>
										<div
											className={`${css.sortIcon} ${
												activeSortDirection === "desc" || activeSortDirection === "descending"
													? css.isActive
													: ""
											}`}
										>
											<ArrowDown size={16} />
										</div>
									</div>
								) : null}
							</span>
						</div>
						{subheaders[id] ? (
							<div className={css.subheaderContainer}>
								{subheaders[id].map((subheader) => {
									const subID = subheader.id;
									const subSortName = subheader.sortName || subID;
									const subIsSortable = subheader.isSortable || true;
									const subHasFilter = activeSort === subSortName;
									return (
										<div
											key={subID}
											className={`${css.subheaderItem} ${subHasFilter ? css.hasFilter : ""} ${
												subIsSortable ? css.subIsSortable : ""
											}`}
										>
											<span
												onClick={() => (subIsSortable ? handleColumnClick(subSortName) : null)}
												className={css.span}
											>
												<div className={`${css.label} ${css.subheaderItem}`}>
													{get(["label"], subheader)}
												</div>

												{subHasFilter && subIsSortable ? (
													<div className={css.sortIconWrapper}>
														<div
															className={`${css.sortIcon} ${
																activeSortDirection === "asc" ||
																activeSortDirection === "ascending"
																	? css.isActive
																	: ""
															}`}
														>
															<ArrowUp size={16} />
														</div>
														<div
															className={`${css.sortIcon} ${
																activeSortDirection === "desc" ||
																activeSortDirection === "descending"
																	? css.isActive
																	: ""
															}`}
														>
															<ArrowDown size={16} />
														</div>
													</div>
												) : null}
											</span>
										</div>
									);
								})}
							</div>
						) : null}
					</div>
				);
			})}
		</div>
	);
};

TableHead.propTypes = {
	onColumnClick: PropTypes.func,
	onUpdateHighlight: PropTypes.func,
	activeSort: PropTypes.string,
	activeSortDirection: PropTypes.string,
	highlightColumn: PropTypes.string,
	defaultCellClassName: PropTypes.string,
	subheaders: PropTypes.object,
	columns: PropTypes.array.isRequired,
	colWidths: PropTypes.string.isRequired,
	currentScreenWidth: PropTypes.number.isRequired,
};

TableHead.defaultProps = {
	defaultCellClassName: "",
	activeSort: null,
	activeSortDirection: null,
	highlightColumn: null,
	subheaders: {},
	onColumnClick: () => {},
	onUpdateHighlight: () => {},
};

export default TableHead;
